<template>
  <v-container fluid>
    <v-skeleton-loader v-if="loading" type="article"></v-skeleton-loader>
    <div
      class="pt-5 pl-md-5 mb-10"
      style="max-width: 1000px"
      v-if="list && !loading"
    >
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        @submit.prevent="submit"
      >
        <v-alert dense outlined type="error" v-if="error">
          <ul>
            <li v-for="item in error" :key="item.msg">{{ $t(item.msg) }}</li>
          </ul>
        </v-alert>
        <div>
          <h1 class="text-h5 mb-5">
            {{ $t("EditAccount") }}
          </h1>
        </div>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              type="text"
              v-model="list.firstName"
              :rules="reqRules"
              v-bind:label="$t('FirstName')"
              hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              type="text"
              v-model="list.lastName"
              v-bind:label="$t('LastName')"
              hide-details="auto"
              :rules="reqRules"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6" cols="12">
            <vue-tel-input-vuetify
              outlined
              v-bind:label="$t('PhoneNumber')"
              :rules="phoneRules"
              v-model="list.phone"
              placeholder=""
              hide-details="auto"
              mode="international"
              :preferredCountries="['US', 'JP']"
            ></vue-tel-input-vuetify>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              readonly
              type="email"
              hide-details="auto"
              v-model="list.email"
              v-bind:label="$t('EmailAddress')"
              append-icon="mdi-pencil"
            >
              <template v-slot:append>
                <v-dialog v-model="dialog" persistent max-width="600px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      dark
                      color="light-blue"
                      fab
                      depressed
                    >
                      <v-icon v-on="on" x-small> mdi-border-color </v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title
                      class="text-h5 light-blue font-weight-regular darken-4 white--text"
                    >
                      {{ $t("UpdateYourEmail") }}
                      <v-spacer></v-spacer>
                      <v-btn
                        color="red darken-1"
                        dark
                        x-small
                        fab
                        @click="closeDialogue"
                      >
                        <v-icon>mdi-window-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-5">
                      <v-form
                        ref="editEmailForm"
                        @submit.prevent="saveMail"
                        v-model="emailValid"
                        lazy-validation
                      >
                        <v-row>
                          <v-col cols="12" md="10">
                            <v-text-field
                              dense
                              v-bind:label="$t('EnterNewMail')"
                              :rules="emailRules"
                              v-model="changeEmail"
                              outlined
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-btn color="blue" dark type="submit">
                              {{ $t("Save") }}
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-form>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-bind:label="$t('Addressline1')"
              :rules="reqRules"
              v-model="list.address"
              hide-details="auto"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-bind:label="$t('Addressline2')"
              hide-details="auto"
              v-model="list.address2"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-bind:label="$t('City')"
              hide-details="auto"
              :rules="reqRules"
              v-model="list.city"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-bind:label="$t('Statesprovinces')"
              hide-details="auto"
              :rules="reqRules"
              v-model="list.state"
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-bind:label="$t('ZIPPostalcode')"
              hide-details="auto"
              :rules="reqRules"
              v-model="list.postalcode"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-bind:label="$t('Country')"
              hide-details="auto"
              :rules="reqRules"
              v-model="list.country"
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              outlined
              v-model="list.about"
              v-bind:label="$t('About')"
              hide-details="auto"
              rows="3"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              large
              color="light-blue"
              dark
              min-width="200px"
              rounded
              type="submit"
            >
              <span class="mr-2">{{ $t("Save") }}</span>
              <v-icon size="small">mdi-arrow-right-thick</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <WaitDialogComponent :waitDialog="waitDialog" />
    </div>
  </v-container>
</template>

<script>
import timeoutMixin from "@/mixins/timeoutMixin.js";
import WaitDialogComponent from "@/components/layout/WaitDialogComponent.vue";
import AuthService from "@/services/AuthService";
import swal from "sweetalert";

export default {
  name: "EditAccountComponent",
  mixins: [timeoutMixin],
  components: { WaitDialogComponent },
  data() {
    return {
      waitDialog: false,
      loading: true,
      list: null,
      valid: null,
      emailValid: null,
      error: null,
      dialog: false,
      changeEmail: null,
      file: null,
      reqRules: [value => !!value || this.$t("string719")],
      emailRules: [
        value => !!value || this.$t("string719"),
        value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || this.$t("string724");
        }
      ],
      phoneRules: [
        value => !!value || this.$t("string719"),
        value => {
          const pattern = /[A-Za-z]+/;
          return !pattern.test(value) || this.$t("string722");
        },
        value =>
          (value.length >= 7 && value.length <= 18) || this.$t("string723")
      ]
    };
  },
  async beforeCreate() {
    if (this.$store.getters.isLoggedIn) {
      var token = this.$store.state.token;
      await AuthService.profileDetailed({
        headers: { Authorization: "Bearer " + token }
      })
        .then(result => {
          this.$store.dispatch("setToken", token);
          this.list = result.data.user;
          this.loading = false;
        })
        .catch(err => {
          this.error = err.response.data.error;
          this.$store.dispatch("logout");
          this.$router.push({ name: "Home" });
        });
    }
  },
  methods: {
    async submit() {
      await this.$refs.form.validate();
      if (this.valid) {
        this.dialog = false;
        this.waitDialog = true;
        await AuthService.changeProfile({
          firstName: this.list.firstName,
          lastName: this.list.lastName,
          phone: this.list.phone,
          about: this.list.about,
          address: this.list.address,
          address2: this.list.address2,
          city: this.list.city,
          state: this.list.state,
          postalcode: this.list.postalcode,
          country: this.list.country
        })
          .then(result => {
            swal({
              icon: "success",
              text: result.data.message,
              button: {
                text: this.$t("Close"),
                className: "swal-close"
              }
            });
            this.$store.dispatch("setUser", result.data.user);
          })
          .catch(err => {
            this.error = err;
            this.$store.dispatch("logout");
            this.$router.push({
              name: "Login"
            });
          });
        setTimeout(() => (this.waitDialog = false), 500);
      }
    },
    closeDialogue() {
      this.dialog = false;
      this.changeEmail = null;
      this.emailValid = null;
    },
    async saveMail() {
      await this.$refs.editEmailForm.validate();
      if (this.emailValid) {
        const email = this.changeEmail;
        swal({
          title: this.$t("string691"),
          text: this.$t("string696"),
          icon: "warning",
          buttons: {
            cancel: "Cancel",
            Proceed: {
              text: this.$t("Proceed"),
              value: true
            }
          }
        }).then(proceed => {
          if (proceed) this.saveMailAction(email);
        });
      }
    },
    async saveMailAction(email) {
      this.waitDialog = true;
      AuthService.changeEmail({
        email
      })
        .then(result => {
          this.waitDialog = false;
          swal({
            icon: "success",
            text: result.data.message,
            button: {
              text: this.$t("Close"),
              className: "swal-close"
            }
          });
          this.$store.dispatch("logout");
          this.$router.push({
            name: "EmailVerification",
            params: { id: result.data.uid }
          });
        })
        .catch(err => {
          this.waitDialog = false;
          this.error = err.response.data.errors;
          this.closeDialogue();
          swal({
            icon: "error",
            text: this.error[0].msg,
            button: {
              text: this.$t("Close"),
              className: "swal-close"
            }
          });
        });
    }
  }
};
</script>

<style></style>
